import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal, TableBody, TableHead, TableRow, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import { X } from "lucide-react";
import { PendingProductImport } from "../../../../data/entities";
import { StyledTableCell, StyledTable } from "../../../../shared/components/CustomTable/Styled";
import LoadingButton from "../../../../shared/components/LoadingButton";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import TextBox from "../../../../shared/components/TextBox";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error as toastError } from "../../../../utils/toast";
import { StyledBackdrop } from "../SharedModalStyled";
import {
    allocateOrderQuantities,
    BulkImportLocalOrder,
    filterPositiveOrderQuantities,
    getTotalQuantity,
    validateOrderQuantity,
    validateRequestedTotal,
} from "./bulkImportAllocation";

interface BulkImportFormState {
    receiver_name: string;
    import_date: Dayjs;
    import_method: string;
    note: string;
}

interface BulkImportModalProps {
    open: boolean;
    item: PendingProductImport | null;
    formState: BulkImportFormState;
    isSubmitting: boolean;
    onClose: () => void;
    onConfirm: (localOrders: BulkImportLocalOrder[]) => void;
    onFormChange: (changes: Partial<BulkImportFormState>) => void;
}

const orderTableColumns = ["Đơn hàng", "Khách hàng", "Số lượng"];

const fieldLabelStyle: React.CSSProperties = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#333739",
    fontFamily: "Noto Sans",
    marginBottom: "4px",
};

const fieldWrapperStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
};

const formGridSx = {
    display: "grid",
    gridTemplateColumns: {
        xs: "1fr",
        sm: "1fr 1fr",
    },
    gap: "14px",
};

const inputSx = {
    "& .MuiInputBase-root": {
        height: "40px",
        fontSize: "13px",
        fontFamily: "Noto Sans",
    },
    "& .MuiInputBase-input": {
        padding: "8px 12px",
    },
};

const BulkImportModal = ({
    open,
    item,
    formState,
    isSubmitting,
    onClose,
    onConfirm,
    onFormChange,
}: BulkImportModalProps) => {
    const [localOrders, setLocalOrders] = useState<BulkImportLocalOrder[]>([]);
    const [quantityError, setQuantityError] = useState<string | null>(null);

    useEffect(() => {
        if (item) {
            setLocalOrders(item.orders.map((o) => ({ ...o })));
            setQuantityError(null);
        }
    }, [item]);

    if (!item) return null;

    const currentTotal = getTotalQuantity(localOrders);
    const hasPositiveQuantity = localOrders.some((order) => Number(order.quantity || 0) > 0);

    const handleTotalChange = (value: string) => {
        const newTotal = parseInternationalToNumber(value, toastError);
        if (!Number.isFinite(newTotal) || newTotal < 0) return;

        const error = validateRequestedTotal(item.orders, newTotal);
        if (error) {
            setQuantityError(error);
            return;
        }

        setQuantityError(null);
        setLocalOrders(allocateOrderQuantities(item.orders, newTotal));
    };

    const handleOrderQuantityChange = (orderId: string, value: string) => {
        const quantity = parseInternationalToNumber(value, toastError);
        if (!Number.isFinite(quantity)) return;

        const originalOrder = item.orders.find((order) => order.sale_order.id === orderId);
        if (!originalOrder) return;

        const error = validateOrderQuantity(originalOrder.quantity, quantity);
        if (error) {
            setQuantityError(`${originalOrder.sale_order.order_name}: ${error}`);
            return;
        }

        setQuantityError(null);
        setLocalOrders((orders) =>
            orders.map((order) =>
                order.sale_order.id === orderId
                    ? { ...order, quantity }
                    : order,
            ),
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "min(980px, 92vw)",
                    maxHeight: "88vh",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    boxShadow: "0 24px 56px rgba(15, 23, 42, 0.24)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    fontFamily: "Noto Sans",
                    outline: "none",
                }}
            >
                <Box
                    sx={{
                        px: "24px",
                        py: "18px",
                        borderBottom: "1px solid #E5EAF1",
                        backgroundColor: "#F8FAFC",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                fontSize: "20px",
                                fontWeight: 700,
                                lineHeight: "28px",
                                color: "#1F2937",
                            }}
                        >
                            Xác nhận đề nghị nhập kho
                        </Box>
                        <Box
                            sx={{
                                mt: "8px",
                                color: "#4B5563",
                                fontSize: "13px",
                                lineHeight: "20px",
                                wordBreak: "break-word",
                            }}
                        >
                            {item.product.name} ({item.product.product_code})
                        </Box>
                    </Box>
                    <IconButton
                        aria-label="Đóng"
                        onClick={onClose}
                        disabled={isSubmitting}
                        sx={{
                            width: "34px",
                            height: "34px",
                            border: "1px solid #D8DEE8",
                            borderRadius: "6px",
                            backgroundColor: "#FFFFFF",
                            color: "#526171",
                            "&:hover": { backgroundColor: "#EEF2F7" },
                        }}
                    >
                        <X size={18} />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        px: "24px",
                        py: "18px",
                        overflowY: "auto",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px",
                            p: "12px 14px",
                            border: "1px solid #D5E3F2",
                            borderRadius: "6px",
                            backgroundColor: "#F8FBFE",
                        }}
                    >
                        <Box sx={{ fontSize: "13px", fontWeight: 700, color: "#0058A0" }}>
                            Tổng số lượng đề nghị nhập kho
                        </Box>
                        <TextBox
                            numeric
                            text={formatInternationalNumber(currentTotal)}
                            onChangeText={handleTotalChange}
                            style={{ width: "140px" }}
                        />
                    </Box>

                    {quantityError && (
                        <Box
                            sx={{
                                px: "12px",
                                py: "8px",
                                border: "1px solid #F2B8B5",
                                borderRadius: "6px",
                                backgroundColor: "#FFF5F5",
                                color: "#C62828",
                                fontSize: "12px",
                                lineHeight: "18px",
                            }}
                        >
                            {quantityError}
                        </Box>
                    )}

                    <Box>
                        <Box sx={{ mb: "8px", fontSize: "13px", fontWeight: 700, color: "#333739" }}>
                            Phân bổ theo đơn hàng
                        </Box>
                        <Box
                            sx={{
                                border: "1px solid #E5EAF1",
                                borderRadius: "6px",
                                overflow: "auto",
                                maxHeight: "300px",
                                backgroundColor: "#FFFFFF",
                                "&::-webkit-scrollbar": { width: "12px", height: "12px" },
                                "&::-webkit-scrollbar-track": {
                                    backgroundColor: "#F1F5F9",
                                    borderRadius: "8px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    backgroundColor: "#94A3B8",
                                    border: "3px solid #F1F5F9",
                                    borderRadius: "8px",
                                },
                                scrollbarColor: "#94A3B8 #F1F5F9",
                                scrollbarWidth: "thin",
                            }}
                        >
                            <StyledTable stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {orderTableColumns.map((col) => (
                                            <StyledTableCell key={col} align="left">
                                                {col}
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {localOrders.map((order) => (
                                        <TableRow key={order.sale_order.id}>
                                            <StyledTableCell align="left">
                                                <StyledNavLink to={`/product-orders/${order.sale_order.id}`}>
                                                    {order.sale_order.order_name}
                                                </StyledNavLink>
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                                {order.sale_order.customer_name}
                                            </StyledTableCell>
                                            <StyledTableCell align="left">
                                                <TextBox
                                                    numeric
                                                    text={formatInternationalNumber(order.quantity)}
                                                    onChangeText={(value) => handleOrderQuantityChange(order.sale_order.id, value)}
                                                    style={{ width: "120px" }}
                                                />
                                            </StyledTableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </StyledTable>
                        </Box>
                    </Box>

                    <Box sx={formGridSx}>
                        <div style={fieldWrapperStyle}>
                            <div style={fieldLabelStyle}>Người nhận</div>
                            <TextField
                                size="small"
                                value={formState.receiver_name}
                                onChange={(e) => onFormChange({ receiver_name: e.target.value })}
                                placeholder="Nhập tên người nhận"
                                sx={inputSx}
                            />
                        </div>

                        <div style={fieldWrapperStyle}>
                            <div style={fieldLabelStyle}>
                                Ngày đề nghị nhập kho <span style={{ color: "#F00" }}>*</span>
                            </div>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                                <DemoContainer
                                    components={["DatePicker"]}
                                    sx={{ paddingTop: "0px", "& .MuiTextField-root": { width: "100%" } }}
                                >
                                    <DatePicker
                                        value={formState.import_date}
                                        onChange={(newVal) =>
                                            onFormChange({ import_date: newVal ?? dayjs() })
                                        }
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: { placeholder: "DD/MM/YYYY", size: "small" },
                                            popper: {
                                                placement: "bottom-end",
                                                sx: {
                                                    "& .MuiPickersDay-root.Mui-selected": {
                                                        backgroundColor: "#0058A0",
                                                    },
                                                },
                                            },
                                        }}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: "40px",
                                                fontSize: "13px",
                                                fontFamily: "Noto Sans",
                                            },
                                            "& .MuiInputBase-input": { padding: "8px 12px" },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </div>

                        <div style={fieldWrapperStyle}>
                            <div style={fieldLabelStyle}>Phương thức đề nghị nhập kho</div>
                            <TextField
                                size="small"
                                value={formState.import_method}
                                onChange={(e) => onFormChange({ import_method: e.target.value })}
                                placeholder="Phương thức đề nghị nhập kho"
                                sx={inputSx}
                            />
                        </div>

                        <div style={fieldWrapperStyle}>
                            <div style={fieldLabelStyle}>Ghi chú</div>
                            <TextField
                                size="small"
                                value={formState.note}
                                onChange={(e) => onFormChange({ note: e.target.value })}
                                placeholder="Ghi chú tùy chọn"
                                sx={inputSx}
                            />
                        </div>
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: "24px",
                        py: "14px",
                        borderTop: "1px solid #E5EAF1",
                        backgroundColor: "#FFFFFF",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        flexShrink: 0,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                        sx={{
                            height: "36px",
                            px: "14px",
                            borderRadius: "5px",
                            textTransform: "none",
                            fontSize: "13px",
                            fontFamily: "Noto Sans",
                            borderColor: "#B8C0CC",
                            color: "#374151",
                            "&:hover": {
                                borderColor: "#0058A0",
                                color: "#0058A0",
                            },
                        }}
                    >
                        Huỷ bỏ
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={() => onConfirm(filterPositiveOrderQuantities(localOrders))}
                        isLoading={isSubmitting}
                        disabled={isSubmitting || !formState.import_date || !!quantityError || !hasPositiveQuantity}
                        text="Xác nhận đề nghị nhập kho"
                        style={{
                            height: "36px",
                            borderRadius: "5px",
                            textTransform: "none",
                            fontSize: "13px",
                            fontFamily: "Noto Sans",
                            backgroundColor: "#0058A0",
                            color: "#FFFFFF",
                            boxShadow: "none",
                        }}
                    />
                </Box>
            </Box>
        </Modal>
    );
};

export default BulkImportModal;
